import { Button } from '@/components/ui/button';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import {
  AdvanceFilter,
  AdvanceFilterFieldType,
  AdvanceFilterConfig,
  FilterTypeOperatorMap,
  AdvanceFilterOperator,
} from '@/types/types';
import { ChevronDown, ChevronUp, Plus, SlidersHorizontal, Trash2, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { FILTER_OPERATOR_OPTIONS, US_STATES_OPTIONS } from '@/utils/constant';
import { v4 as uuid } from 'uuid';
import { FormFieldSelect, Option } from '@/components/FormFieldSelect';
import { FormProvider, useForm } from 'react-hook-form';
import { AdvanceFilterValueField } from '@/components/AdvanceFilterComponents';
import { convertISODateToMMDDYYYY } from '@/lib/utils';

const DATE_FIELD_VALUES = new Set(['createdOn', 'lastServeDate']);

const hiFilterConfig: AdvanceFilterConfig[] = [
  { value: 'createdOn', label: 'Created On Date', fieldType: AdvanceFilterFieldType.DATE, inputType: 'DATE' },
  { value: 'lastServeDate', label: 'Last Serve Date', fieldType: AdvanceFilterFieldType.DATE, inputType: 'DATE' },
  { value: 'caseName', label: 'Case Name', fieldType: AdvanceFilterFieldType.STRING, inputType: 'INPUT' },
  { value: 'fileNumber', label: 'File Number', fieldType: AdvanceFilterFieldType.STRING, inputType: 'INPUT' },
  { value: 'clientContact', label: 'Client Contact', fieldType: AdvanceFilterFieldType.STRING, inputType: 'INPUT' },
  { value: 'mortgageCompany', label: 'Mortgage Company', fieldType: AdvanceFilterFieldType.STRING, inputType: 'INPUT' },
  { value: 'propertyAddress', label: 'Property Address', fieldType: AdvanceFilterFieldType.STRING, inputType: 'INPUT' },
  { value: 'city', label: 'City', fieldType: AdvanceFilterFieldType.STRING, inputType: 'INPUT' },
  { value: 'state', label: 'State', fieldType: AdvanceFilterFieldType.ID, inputType: 'SELECT' },
  { value: 'zipCode', label: 'Zip Code', fieldType: AdvanceFilterFieldType.ID, inputType: 'INPUT' },
  {
    value: 'ownerOrDefendantName',
    label: 'Owner / Defendant Name',
    fieldType: AdvanceFilterFieldType.STRING,
    inputType: 'INPUT',
  },
];

// Non-date fields that cannot be selected more than once
const uniqueFieldValues = new Set(hiFilterConfig.filter((c) => !DATE_FIELD_VALUES.has(c.value)).map((c) => c.value));

export const HIAdvanceFilters = ({
  onClose,
  filters,
  setFilters,
  hiListViewFilters,
  open,
}: {
  onClose: () => void;
  filters: AdvanceFilter[];
  setFilters: (filters: AdvanceFilter[]) => void;
  hiListViewFilters: string[];
  open: boolean;
}) => {
  const form = useForm({ mode: 'onChange' });

  const fieldDefaultOptions: Record<string, Option[]> = {
    state: US_STATES_OPTIONS,
  };

  const [selectedFilters, setSelectedFilter] = useState<
    {
      id: string;
      operators?: AdvanceFilterOperator[];
      filter?: AdvanceFilter;
      isExpanded?: boolean;
    }[]
  >([]);

  useEffect(() => {
    if (filters) {
      setSelectedFilter(
        filters
          .filter((filter) => filter.field && !hiListViewFilters.includes(filter.field))
          .map((filter) => ({
            id: uuid(),
            filter,
            isExpanded: false,
            operators: filter?.fieldType
              ? (hiFilterConfig.find((config) => config.value === filter.field)?.customFilterTypeOperator ??
                FilterTypeOperatorMap[filter?.fieldType])
              : [],
          }))
      );
    }
  }, [filters]);

  const onDeleteFilter = (id: string) => setSelectedFilter((prev) => prev.filter((f) => f.id !== id));

  const toggleExpand = (id: string, expand: boolean) =>
    setSelectedFilter((prev) => prev.map((f) => (f.id === id ? { ...f, isExpanded: expand } : f)));

  // For the "Search Fields" dropdown: exclude non-date fields already used in OTHER rows
  const getAvailableFieldOptions = (currentRowId: string) =>
    hiFilterConfig.filter((c) => {
      if (DATE_FIELD_VALUES.has(c.value)) return true; // date fields can be added multiple times
      return !selectedFilters.some(
        (sf) => sf.id !== currentRowId && sf.filter?.field === c.value && uniqueFieldValues.has(c.value)
      );
    });

  // Allow adding more filters as long as unique non-date fields aren't exhausted
  const canAddFilter = () => {
    const usedUniqueFields = new Set(
      selectedFilters.map((sf) => sf.filter?.field).filter((f): f is string => !!f && uniqueFieldValues.has(f))
    );
    return usedUniqueFields.size < uniqueFieldValues.size;
  };

  return (
    <Sheet modal={true} open={open}>
      <SheetContent
        hideCloseButton={true}
        className="w-[80vw] md:w-[500px] scrollbar-hide bg-white px-4 py-7 flex flex-col gap-6"
        side="right"
        tabIndex={-1}
      >
        <div className="flex w-full">
          <div className="w-full">
            <div className="flex justify-between">
              <div className="text-[20px]">Advance Search</div>
              <div className="border-[1px] p-[6px] rounded-md cursor-pointer" onClick={onClose}>
                <X color="#676767" className="h-[20px] w-[20px]" />
              </div>
            </div>
            <hr className="mt-4 border-[#CAC8C8]" />
            <div className="mt-5 flex justify-between items-center">
              <div className="flex gap-2 items-center">
                <SlidersHorizontal className="w-5 h-5 text-primary" />
                <span className="text-[16px] font-medium text-primary">Advance Search Filters</span>
              </div>
              {canAddFilter() && (
                <Button
                  className="h-8 flex gap-2"
                  onClick={() => {
                    setSelectedFilter((prev) => [...prev, { id: uuid(), isExpanded: true, operators: [] }]);
                  }}
                >
                  <Plus className="w-4 h-4" />
                  <span className="text-[14px]">Add Filter</span>
                </Button>
              )}
            </div>
          </div>
        </div>

        <FormProvider {...form}>
          <div className="flex-1 flex flex-col overflow-y-auto gap-6 custom-scrollbar">
            {selectedFilters.map((selectedFilter) => {
              const config = hiFilterConfig.find((c) => c.value === selectedFilter.filter?.field);
              return (
                <div
                  key={selectedFilter.id}
                  className={`p-3 flex flex-col gap-6 border-[1px] rounded-xl border-[#969696] relative ${selectedFilter.isExpanded ? 'cursor-default' : 'cursor-pointer'}`}
                  onClick={() => !selectedFilter.isExpanded && toggleExpand(selectedFilter.id, true)}
                >
                  {selectedFilter.isExpanded ? (
                    <>
                      <div>
                        <FormFieldSelect
                          sortOptions
                          options={getAvailableFieldOptions(selectedFilter.id)}
                          name={`${selectedFilter.id}_field`}
                          defaultValue={selectedFilter.filter?.field}
                          label="Search Fields"
                          onChange={(newValue) => {
                            const found = hiFilterConfig.find((c) => c.value === newValue);
                            if (found) {
                              const operators =
                                found.customFilterTypeOperator ?? FilterTypeOperatorMap[found.fieldType] ?? [];
                              setSelectedFilter((prev) =>
                                prev.map((sf) =>
                                  sf.id === selectedFilter.id
                                    ? {
                                        ...sf,
                                        operators,
                                        filter: {
                                          ...sf.filter,
                                          field: newValue,
                                          value: '',
                                          fieldType: found.fieldType,
                                          operator: operators[0],
                                        },
                                      }
                                    : sf
                                )
                              );
                            }
                          }}
                          placeholder="Select Search fields"
                        />
                      </div>
                      {selectedFilter?.filter && (
                        <>
                          <FormFieldSelect
                            options={FILTER_OPERATOR_OPTIONS.filter(
                              (op) =>
                                selectedFilter.filter?.fieldType &&
                                FilterTypeOperatorMap[selectedFilter.filter.fieldType].includes(op.value)
                            )}
                            name={`${selectedFilter.id}_operator`}
                            defaultValue={selectedFilter.filter?.operator}
                            label="Criteria"
                            placeholder="Select Criteria"
                            disabled={selectedFilter.operators?.length === 1}
                            onChange={(newValue) => {
                              setSelectedFilter((prev) =>
                                prev.map((sf) =>
                                  sf.id === selectedFilter.id
                                    ? {
                                        ...sf,
                                        filter: {
                                          ...sf.filter,
                                          operator: newValue as AdvanceFilterOperator,
                                        },
                                      }
                                    : sf
                                )
                              );
                            }}
                          />
                          <AdvanceFilterValueField
                            inputType={
                              hiFilterConfig.find((c) => c.value === selectedFilter.filter?.field)?.inputType ?? 'INPUT'
                            }
                            name={`${selectedFilter.id}_value`}
                            value={selectedFilter.filter.value ?? ''}
                            options={
                              selectedFilter.filter.field
                                ? (fieldDefaultOptions[selectedFilter.filter.field] ?? [])
                                : []
                            }
                            onValueChange={(value) => {
                              setSelectedFilter((prev) =>
                                prev.map((sf) =>
                                  sf.id === selectedFilter.id ? { ...sf, filter: { ...sf.filter, value } } : sf
                                )
                              );
                            }}
                          />
                        </>
                      )}
                    </>
                  ) : (
                    <div className="flex flex-col">
                      <span className="text-sm">{config?.label}</span>
                      <div className="text-[#969696]">
                        <span className="text-sm">
                          {FILTER_OPERATOR_OPTIONS.find((op) => op.value === selectedFilter.filter?.operator)?.label}
                        </span>
                        {selectedFilter.filter?.value && selectedFilter.filter.field && (
                          <span className="break-words">
                            &nbsp;•&nbsp;
                            {config?.inputType === 'INPUT'
                              ? selectedFilter.filter.value
                              : config?.inputType === 'DATE'
                                ? convertISODateToMMDDYYYY(selectedFilter.filter.value)
                                : config?.inputType === 'SELECT'
                                  ? fieldDefaultOptions?.[selectedFilter.filter.field]?.find(
                                      (opt) => opt.value === selectedFilter.filter?.value
                                    )?.label
                                  : ''}
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                  <div className="absolute top-3 right-3 cursor-pointer flex items-center gap-3">
                    {selectedFilter.filter ? (
                      !selectedFilter.isExpanded ? (
                        <ChevronDown
                          className="h-5 w-5"
                          color="#969696"
                          onClick={() => toggleExpand(selectedFilter.id, true)}
                        />
                      ) : (
                        <ChevronUp
                          className="h-5 w-5"
                          color="#969696"
                          onClick={() => toggleExpand(selectedFilter.id, false)}
                        />
                      )
                    ) : null}
                    <Trash2 color="#D72222" className="h-5 w-5" onClick={() => onDeleteFilter(selectedFilter.id)} />
                  </div>
                </div>
              );
            })}
          </div>
        </FormProvider>

        <div className="bg-white sticky bottom-0">
          <div className="flex justify-end gap-3">
            {selectedFilters.length > 1 && (
              <Button
                variant="outline"
                onClick={() => {
                  setSelectedFilter([]);
                  setFilters(filters.filter((f) => f.field && hiListViewFilters.includes(f.field)));
                  onClose();
                }}
                className="px-4 py-3"
              >
                Clear All
              </Button>
            )}
            <Button
              className="px-7 py-3"
              onClick={() => {
                setFilters(
                  selectedFilters
                    .filter((sf): sf is { id: string; filter: AdvanceFilter } => !!sf.filter && !!sf.filter.value)
                    .map((sf) => sf.filter)
                    .concat(filters.filter((f) => f.field && hiListViewFilters.includes(f.field)))
                );
                onClose();
              }}
            >
              Apply
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};
